/**
 * @typedef {'employee' | 'service'} ScheduleCalendarType
 */

/**
 * @typedef {'pending_approval' | 'approved' | 'rejected' | 'completed'} ScheduleEventStatus
 */

/**
 * @typedef {Object} ScheduleEventLinks
 * @property {string} [lead_id]
 * @property {string} [lead_type]
 * @property {string} [deal_id]
 * @property {string} [vehicle_id]
 * @property {string} [service_request_id]
 * @property {string} [customer_name]
 * @property {string} [customer_phone]
 * @property {string} [customer_email]
 * @property {string} [vehicle_info]
 */

/**
 * @typedef {Object} ScheduleEvent
 * @property {string} id
 * @property {ScheduleCalendarType} calendar_type
 * @property {string} event_type
 * @property {string} title
 * @property {string} description
 * @property {string} start_at
 * @property {string} end_at
 * @property {number} duration_minutes
 * @property {string} [duration_preset]
 * @property {ScheduleEventStatus} status
 * @property {string} source
 * @property {ScheduleEventLinks} links
 * @property {string} created_at
 * @property {string} updated_at
 */

/**
 * @typedef {Object} PendingApprovalItem
 * @property {string} approval_id
 * @property {ScheduleCalendarType} calendar_type
 * @property {string} requested_at
 * @property {string} source
 * @property {ScheduleEvent} event
 */

/**
 * @typedef {Object} OutreachScheduleRequest
 * @property {string} request_id
 * @property {string} event_type
 * @property {string} event_type_label
 * @property {ScheduleCalendarType} calendar_type
 * @property {string} lead_id
 * @property {string} [lead_type]
 * @property {string} [deal_id]
 * @property {string} [deal_label]
 * @property {string} [vehicle_id]
 * @property {string} [service_request_id]
 * @property {string} preferred_start_at
 * @property {number} duration_minutes
 * @property {string} duration_label
 * @property {string} notes
 * @property {string} source
 * @property {string} source_label
 * @property {string} status
 * @property {string} created_at
 * @property {string} customer_name
 * @property {string} [customer_phone]
 * @property {string} [customer_email]
 * @property {string} vehicle_info
 */

export const EMPLOYEE_EVENT_TYPES = [
  { value: 'customer_test_drive', label: 'Customer Test Drive', needsLinks: true },
  { value: 'vehicle_delivery', label: 'Vehicle Delivery', needsLinks: true, needsDeliveryDeal: true },
  { value: 'lead_meet_up', label: 'Lead Meet up', needsLinks: true },
  { value: 'internal_meeting', label: 'Internal Meeting', needsLinks: false },
  { value: 'custom_task', label: 'Custom Task', needsLinks: false },
  { value: 'routine_maintenance_downtime', label: 'Routine Maintenance Downtime', needsLinks: false },
];

export const SERVICE_EVENT_TYPES = [
  { value: 'vehicle_drop_off', label: 'Vehicle Drop off', needsServiceRequest: true },
  { value: 'vehicle_servicing', label: 'Vehicle Servicing', needsServiceRequest: true },
  { value: 'vehicle_pick_up', label: 'Vehicle Pick up', needsServiceRequest: true },
];

export const EMPLOYEE_DURATION_PRESETS = [
  { value: '15m', label: '15 minutes' },
  { value: '30m', label: '30 minutes' },
  { value: '1h', label: '1 hour' },
  { value: '2h', label: '2 hours' },
  { value: 'custom', label: 'Custom' },
];

export const SERVICE_DURATION_PRESETS = [
  { value: '2h', label: '2 hours' },
  { value: '4h', label: '4 hours' },
  { value: '6h', label: '6 hours' },
  { value: 'custom', label: 'Custom' },
];

export const STATUS_STYLES = {
  pending_approval: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  approved: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  rejected: 'bg-red-500/15 text-red-400 border-red-500/30',
  completed: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
};

export const STATUS_LABELS = {
  pending_approval: 'Pending Approval',
  approved: 'Approved',
  rejected: 'Rejected',
  completed: 'Completed',
};